// A small, bundled catalog of well-studied SNP↔condition associations, plus
// the genotype-matching logic behind the "Conditions" tab.
//
// ── WHAT THIS IS ─────────────────────────────────────────────────────────────
// A *starter* catalog of associations that are widely replicated and commonly
// genotyped on consumer arrays. It is hand-authored and deliberately small; it
// is not a mirror of the GWAS Catalog, ClinVar, or SNPedia, and it carries no
// effect sizes it can't state honestly. Before treating any entry as settled,
// check it against the primary sources named in `sources`.
//
// ── WHAT THIS IS NOT ─────────────────────────────────────────────────────────
// Not a diagnostic, and not a risk calculator. There is deliberately no
// aggregate "risk score": summing odds ratios across markers implies a
// precision that genotype-only data does not support. Each marker is reported
// on its own, as "your call vs. the allele the literature reports".
//
// ── SCOPE LIMITS (why your condition may be missing) ─────────────────────────
//  - Indel and repeat-expansion variants (CFTR F508del, HTT CAG, …) are out of
//    scope: array encodings for them vary too much to match reliably.
//  - Genes whose clinical variants are mostly rare and array-absent (BRCA1/2,
//    LDLR, …) are out of scope. Absence here means nothing about your risk.
//  - Consumer arrays type ~0.02% of the genome. A "no risk allele" result is
//    the absence of one specific finding, not the absence of risk.
//
// ── STRAND ───────────────────────────────────────────────────────────────────
// `alleles` are given on the dbSNP/forward orientation. Consumer exports vary,
// so `matchMarker` detects a strand flip by comparing the observed call to both
// the listed pair and its complement.
//
// Palindromic SNPs (A/T, C/G) read identically on both strands, so a flip is
// undetectable — but only *homozygous* calls are actually ambiguous. A
// heterozygous call carries one of each allele regardless of orientation, so it
// still resolves to exactly one copy. Homozygous palindromic calls therefore get
// their own status and report both possible counts rather than picking one.

export type ConditionCategory =
  | 'metabolic'
  | 'cardiovascular'
  | 'autoimmune'
  | 'neurological'
  | 'cancer'
  | 'hematologic'
  | 'pharmacogenomic'
  | 'dermatologic'
  | 'respiratory'
  | 'trait';

/** How much weight a finding can bear — drives the UI's framing, not a score. */
export type Impact =
  /** Benign, non-medical (earwax, muscle fiber type). */
  | 'trait'
  /** Real but small effect; population-level, weak for individuals. */
  | 'modest'
  /** Well-replicated, meaningful effect size. */
  | 'notable'
  /** Clinically actionable — needs clinical-grade confirmation, not an array. */
  | 'clinical';

export interface ConditionMarker {
  rsid: string;
  gene: string;
  /** The SNP's two alleles, dbSNP/forward orientation. */
  alleles: [string, string];
  /** The allele the reported association attaches to. */
  effectAllele: string;
  /**
   * 'trait' is for benign, non-medical outcomes (muscle fibre type, earwax):
   * carrying the allele is neither good nor bad, and the UI must not colour it
   * as a risk hit.
   */
  direction: 'risk' | 'protective' | 'trait';
  /** Plain-English statement of the reported effect. */
  effect: string;
  note?: string;
}

export interface Condition {
  id: string;
  name: string;
  /** Extra search terms — synonyms, gene names, common phrasings. */
  aliases: string[];
  category: ConditionCategory;
  impact: Impact;
  summary: string;
  markers: ConditionMarker[];
  /** Where to go to verify. */
  sources: string;
  /** Condition-level caveat, shown above the markers. */
  caveat?: string;
}

export const CATEGORY_LABELS: Record<ConditionCategory, string> = {
  metabolic: 'Metabolic',
  cardiovascular: 'Cardiovascular',
  autoimmune: 'Autoimmune',
  neurological: 'Neurological',
  cancer: 'Cancer',
  hematologic: 'Blood',
  pharmacogenomic: 'Drug response',
  dermatologic: 'Skin',
  respiratory: 'Respiratory',
  trait: 'Trait',
};

export const IMPACT_LABELS: Record<Impact, string> = {
  trait: 'benign trait',
  modest: 'small effect',
  notable: 'well replicated',
  clinical: 'clinically actionable',
};
